import React, { JSX, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import LoginPage from "./login/LoginPage";
import CambiarPasswordObligatorioPage from "./login/CambiarPasswordObligatorioPage";
import DashboardPage from "./dashboard/DashboardPage";
import DashboardPage2 from "./dashboard/DashboardPage2";
import DashboardPage3 from "./dashboard/DashboardPage3";
import DashboardPage4 from "./dashboard/DashboardPage4";
import DashboardPage5 from "./dashboard/DashboardPage5";
import DashboardPage6 from "./dashboard/DashboardPage6";
import DashboardPage7 from "./dashboard/DashboardPage7";
import AdminSesionesPage from "./admin/AdminSesionesPage";
import OecePage from "./oece/OecePage";
import NotificacionesPage from "./notificaciones/NotificacionesPage";
import api from "./services/api.service";

const PrivateRoute = ({ children }: { children: JSX.Element }) => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!token) return;

    const checkMe = () => {
      api
        .get("/auth/me")
        .then((resp) => {
          // Cambio de contraseña obligatorio pendiente (ver backend requirePasswordOk):
          // se fuerza la pantalla aunque el usuario ya tuviera el token guardado.
          if (
            resp.data?.debeCambiarPassword &&
            location.pathname !== "/cambiar-password-obligatorio"
          ) {
            navigate("/cambiar-password-obligatorio", { replace: true });
          }
        })
        .catch(() => {});
    };

    // Dispara inmediatamente al montar — detecta kick en cuanto carga la página
    checkMe();
    // Luego cada 10s para catch rápido si alguien entra desde otro dispositivo
    const interval = setInterval(checkMe, 10000);
    return () => clearInterval(interval);
  }, [token, navigate, location.pathname]);

  return token ? children : <Navigate to="/login" replace />;
};

// Solo para el rol OECE. No muestra nada hasta que el backend confirma la sesión y el rol,
// así un token viejo guardado en el navegador no deja ver la página un instante
// antes de que el 401 mande al login.
const OeceRoute = ({ children }: { children: JSX.Element }) => {
  const token = localStorage.getItem("token");
  const [estado, setEstado] = useState<"verificando" | "ok" | "otroRol" | "fuera">("verificando");

  useEffect(() => {
    if (!token) return;
    api
      .get("/auth/me")
      // el cambio de contraseña pendiente lo resuelve PrivateRoute
      .then((resp) => setEstado(resp.data?.rol === "OECE" ? "ok" : "otroRol"))
      .catch(() => setEstado("fuera"));
  }, [token]);

  if (!token || estado === "fuera") return <Navigate to="/login" replace />;
  if (estado === "otroRol") return <Navigate to="/" replace />;
  if (estado === "verificando") {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  return <PrivateRoute>{children}</PrivateRoute>;
};

// Para los roles NOTIFICACIONES y NOTIFICA_ABOGADO. Mismo patrón que OeceRoute: no
// muestra nada hasta que el backend confirma la sesión y el rol, para que un token
// viejo no deje ver la página un instante antes de que el 401 mande al login.
const NotificacionesRoute = ({ children }: { children: JSX.Element }) => {
  const token = localStorage.getItem("token");
  const [estado, setEstado] = useState<"verificando" | "ok" | "otroRol" | "fuera">("verificando");

  useEffect(() => {
    if (!token) return;
    api
      .get("/auth/me")
      .then((resp) =>
        setEstado(
          ["NOTIFICACIONES", "NOTIFICA_ABOGADO"].includes(resp.data?.rol) ? "ok" : "otroRol"
        )
      )
      .catch(() => setEstado("fuera"));
  }, [token]);

  if (!token || estado === "fuera") return <Navigate to="/login" replace />;
  if (estado === "otroRol") return <Navigate to="/" replace />;
  if (estado === "verificando") {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  return <PrivateRoute>{children}</PrivateRoute>;
};

const BossRoute = ({ children }: { children: JSX.Element }) => {
  const token = localStorage.getItem("token");
  const rol = localStorage.getItem("rol");
  if (!token) return <Navigate to="/login" replace />;
  if (rol !== "BOSS") return <Navigate to="/" replace />;
  return children;
};

const HomeRedirect = () => {
  const rol = localStorage.getItem("rol");

  if (rol === "BOSS")     return <Navigate to="/admin/sesiones" replace />;
  if (rol === "PROCESOS") return <DashboardPage2 />;
  if (rol === "INMUEBLE") return <DashboardPage3 />;
  if (rol === "LIBERTAD") return <DashboardPage4 />;
  if (rol === "COMERCIAL")return <DashboardPage5 />;
  if (rol === "PENAL")    return <DashboardPage6 />;
  if (rol === "INCAUTA")  return <DashboardPage7 />;
  if (rol === "OECE")     return <Navigate to="/oece" replace />;
  if (rol === "NOTIFICACIONES")  return <Navigate to="/notificaciones" replace />;
  if (rol === "NOTIFICA_ABOGADO") return <Navigate to="/notificaciones" replace />;

  // AREQUIPA y cualquier otro rol no mapeado → dashboard genérico
  return <DashboardPage />;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          path="/cambiar-password-obligatorio"
          element={
            <PrivateRoute>
              <CambiarPasswordObligatorioPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/"
          element={
            <PrivateRoute>
              <HomeRedirect />
            </PrivateRoute>
          }
        />

        <Route
          path="/expedientes/:id"
          element={
            <PrivateRoute>
              <DashboardPage2 />
            </PrivateRoute>
          }
        />

        <Route
          path="/oece"
          element={
            <OeceRoute>
              <OecePage />
            </OeceRoute>
          }
        />

        <Route
          path="/notificaciones"
          element={
            <NotificacionesRoute>
              <NotificacionesPage />
            </NotificacionesRoute>
          }
        />

        <Route
          path="/admin/sesiones"
          element={
            <BossRoute>
              <AdminSesionesPage />
            </BossRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
